import React, { useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import { AlertUnsavedData, Modal, ModalSize, NotificationToaster } from "components/notifications";
import { FormTextInput } from "components/form-fields";
import { Button, ButtonSave } from "components/buttons";
import { useDirtyData } from "hooks/useDirtyData";
import { useValidation } from "hooks/useValidation";
import { PcnService } from "services/PcnService";
import { Intent } from "@blueprintjs/core";

// Column length mirrors the SQL NVARCHAR limit enforced server-side.
const statusCommentMaxLength = 50;
const badRequestCode = 400;

const defaultModel = {
    id: 0,
    statusComment: ""
};

export function PcnStatusCommentModal(
    {
        showModal,
        modalTitle,
        isUpdate = false,
        loading = false,
        onSave,
        onCloseModal,
        statusCommentDetails = defaultModel
    }
) {
    const [original, setOriginal] = useState(defaultModel);
    const [current, setCurrent] = useState(defaultModel);
    const [saving, setSaving] = useState(false);
    const [blockModalClose, setBlockModalClose] = useState(false);
    const isDirty = useDirtyData(original, current);
    const [isValid, errors, validate] = useValidation();

    useEffect(loadDetails, [statusCommentDetails, showModal]);

    function loadDetails() {
        if (!showModal) {
            return;
        }

        const mapped = {
            id: statusCommentDetails.id || defaultModel.id,
            statusComment: statusCommentDetails.statusComment || defaultModel.statusComment
        };

        setOriginal(mapped);
        setCurrent(mapped);
    }

    function onFieldChange(fieldName, passedValue) {
        setCurrent({
            ...current,
            [fieldName]: passedValue
        });
    }

    useEffect(runValidation, [current, showModal, loading]);

    function runValidation() {
        if (!showModal || loading) {
            return;
        }

        const rules = [
            { fieldName: "statusComment", required: true, maxLength: statusCommentMaxLength }
        ];

        validate(rules, current);
    }

    function onSaveClick() {
        if (!isValid || !isDirty || saving || loading) {
            return;
        }

        const request = {
            statusComment: current.statusComment
        };

        setSaving(true);

        const savePromise = isUpdate
            ? PcnService.updateStatusComment(current.id, request)
            : PcnService.createStatusComment(request);

        savePromise
            .then(() => {
                NotificationToaster.show(Intent.SUCCESS, isUpdate ? "Status comment updated successfully." : "Status comment created successfully.");
                onSaveComplete();
            })
            .catch((error) => {
                if (error?.response?.status === badRequestCode) {
                    NotificationToaster.show(Intent.WARNING, error.response.data);
                } else {
                    NotificationToaster.show(Intent.DANGER, "Could not save the status comment. Please try again.");
                }
            })
            .finally(() => {
                setSaving(false);
            });
    }

    function onSaveComplete() {
        resetModal();
        onSave();
    }

    function onCloseClick(forceClose = false) {
        if (isDirty && !forceClose) {
            setBlockModalClose(true);
            return;
        }

        resetModal();

        if (onCloseModal) {
            onCloseModal();
        }
    }

    function resetModal() {
        setBlockModalClose(false);
        setOriginal(defaultModel);
        setCurrent(defaultModel);
    }

    return (
        <Fragment>
            <Modal
                isOpen={showModal}
                onClose={() => onCloseClick(false)}
                clickOutsideClose={true}
                title={modalTitle}
                size={ModalSize.SMALL}
                updateStateOnClose={false}
                footer={(
                    <div className="bp6-dialog-footer-actions">
                        <Button
                            text="Cancel"
                            intent={Intent.SECONDARY}
                            onClick={() => onCloseClick(true)}
                        />
                        <ButtonSave
                            id="save-status-comment-button"
                            onClick={onSaveClick}
                            simpleDisabled={!isValid || loading || !isDirty}
                            disabled={saving}
                            text={"Save"}
                        />
                    </div>
                )}
            >
                <FormTextInput
                    id="input-status-comment"
                    disabled={saving}
                    loading={loading}
                    value={current.statusComment}
                    headingText="Status Comment"
                    placeholder="Please type the status comment here"
                    onChange={(event) => onFieldChange("statusComment", event.target.value)}
                    dangerHelperText={errors.statusComment}
                    maxLength={statusCommentMaxLength}
                    large={true}
                    fill={true}
                />
            </Modal>

            <AlertUnsavedData
                isDirty={isDirty}
                isDirtySamePage={blockModalClose}
                onCancelLeave={() => setBlockModalClose(false)}
                onConfirmLeave={() => onCloseClick(true)}
            />
        </Fragment>
    );
}

PcnStatusCommentModal.propTypes = {
    showModal: PropTypes.bool.isRequired,
    modalTitle: PropTypes.string.isRequired,
    isUpdate: PropTypes.bool,
    loading: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    onCloseModal: PropTypes.func.isRequired,
    statusCommentDetails: PropTypes.object
};
